import { MarketPlace } from '@amzn/fire-tv-campaign-automation-service';

export type OverlayFieldType = 'toggle-text' | 'toggle-dropdown';

interface OverlayFieldConfig {
  key: string;
  label: string;
  type: OverlayFieldType;
  maxLength?: number;
}

export interface OverlayToggleTextConfig {
  label: string;
  dataTestId: string;
  placeholder: string;
  placeholderByMarketplace?: Partial<Record<MarketPlace, string>>;
  maxLength?: number;
}

export interface OverlayToggleDropDownConfig {
  label: string;
  dataTestId: string;
  placeholder: string;
  optionsByMarketplace?: Partial<Record<MarketPlace, Array<{ name: string; value: string }>>>;
  defaultOptions?: Array<{ name: string; value: string }>;
}

export const OVERLAY_FIELDS: OverlayFieldConfig[] = [
  { key: 'contentBadge', label: 'Content Badge', type: 'toggle-text', maxLength: 40 },
  { key: 'disclaimer', label: 'Disclaimer', type: 'toggle-text', maxLength: 75 },
  { key: 'contentRating', label: 'Content Rating', type: 'toggle-dropdown' },
];

const PLACEHOLDERS: Partial<Record<MarketPlace, { contentBadge: string; disclaimer: string }>> = {
  [MarketPlace.AU]: { contentBadge: 'e.g. New, Exclusive', disclaimer: 'e.g. Limited time only' },
  [MarketPlace.CA]: { contentBadge: 'e.g. New, Exclusive', disclaimer: 'e.g. Limited time only' },
  [MarketPlace.DE]: { contentBadge: 'z.B. Neu, Exklusiv', disclaimer: 'z.B. Nur für kurze Zeit' },
  [MarketPlace.ES]: {
    contentBadge: 'p. ej. Nuevo, Exclusivo',
    disclaimer: 'p. ej. Por tiempo limitado',
  },
  [MarketPlace.FR]: { contentBadge: 'ex. Nouveau, Exclusif', disclaimer: 'ex. Durée limitée' },
  [MarketPlace.IN]: { contentBadge: 'e.g. New, Exclusive', disclaimer: 'e.g. Limited time only' },
  [MarketPlace.IT]: {
    contentBadge: 'es. Nuovo, Esclusivo',
    disclaimer: 'es. Solo per un periodo limitato',
  },
  [MarketPlace.JP]: { contentBadge: '例: 新着, 独占', disclaimer: '例: 期間限定' },
  [MarketPlace.MX]: {
    contentBadge: 'p. ej. Nuevo, Exclusivo',
    disclaimer: 'p. ej. Por tiempo limitado',
  },
  [MarketPlace.UK]: { contentBadge: 'e.g. New, Exclusive', disclaimer: 'e.g. Limited time only' },
  [MarketPlace.US]: { contentBadge: 'e.g. New, Exclusive', disclaimer: 'e.g. Limited time only' },
};

const CONTENT_RATINGS: Partial<Record<MarketPlace, string[]>> = {
  [MarketPlace.AU]: ['G', 'PG', 'M', 'MA15+', 'R18+'],
  [MarketPlace.CA]: ['G', 'PG', '14A', '18A', 'R'],
  [MarketPlace.DE]: ['0', '6', '12', '16', '18'],
  [MarketPlace.ES]: ['APTA', '7', '12', '16', '18'],
  [MarketPlace.FR]: ['U', '10', '12', '16', '18'],
  [MarketPlace.IN]: ['U', 'U/A 7+', 'U/A 13+', 'U/A 16+', 'A'],
  [MarketPlace.IT]: ['T', 'VM6', 'VM14', 'VM18'],
  [MarketPlace.JP]: ['G', 'PG12', 'R15+', 'R18+'],
  [MarketPlace.MX]: ['AA', 'A', 'B', 'B-15', 'C', 'D'],
  [MarketPlace.UK]: ['U', 'PG', '12A', '12', '15', '18'],
  [MarketPlace.US]: ['G', 'PG', 'PG-13', 'R', 'NC-17'],
};

const toOptions = (ratings: string[]) => ratings.map((r) => ({ name: r, value: r }));

const getPlaceholdersByField = (
  field: 'contentBadge' | 'disclaimer',
): Partial<Record<MarketPlace, string>> =>
  Object.fromEntries(Object.entries(PLACEHOLDERS).map(([mp, p]) => [mp, p[field]])) as Partial<
    Record<MarketPlace, string>
  >;

export const OVERLAY_FIELDS_CONFIG: {
  contentBadge: OverlayToggleTextConfig;
  disclaimer: OverlayToggleTextConfig;
  contentRating: OverlayToggleDropDownConfig;
} = {
  contentBadge: {
    label: 'Content Badge',
    dataTestId: 'overlay-content-badge',
    placeholder: 'e.g. New, Exclusive',
    placeholderByMarketplace: getPlaceholdersByField('contentBadge'),
    maxLength: 40,
  },
  disclaimer: {
    label: 'Disclaimer',
    dataTestId: 'overlay-disclaimer',
    placeholder: 'e.g. Limited time only',
    placeholderByMarketplace: getPlaceholdersByField('disclaimer'),
    maxLength: 75,
  },
  contentRating: {
    label: 'Content Rating',
    dataTestId: 'overlay-content-rating',
    placeholder: 'Select rating',
    optionsByMarketplace: Object.fromEntries(
      Object.entries(CONTENT_RATINGS).map(([mp, ratings]) => [mp, toOptions(ratings)]),
    ) as Partial<Record<MarketPlace, Array<{ name: string; value: string }>>>,
    defaultOptions: toOptions(CONTENT_RATINGS[MarketPlace.US]!),
  },
};
